import React from 'react';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { Shield, Mail } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <PublicLayout>
      <SEO 
        title="Privacy Policy - ARCA"
        description="Read the Africa Respiratory Care Association's Privacy Policy to understand how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, ARCA, personal information"
      />
      
      {/* Hero Section */}
      <section className="position-relative text-white py-5" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <Shield size={60} className="mb-3" />
              <h1 className="display-4 fw-bold mb-3">Privacy Policy</h1>
              <p className="lead mb-0">
                Your privacy and data protection are important to us
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card shadow-sm border-0">
                <div className="card-body p-4 p-lg-5">
                  
                  {/* Header Information */}
                  <div className="mb-4 pb-4 border-bottom">
                    <h2 className="h4 fw-bold mb-3" style={{ color: '#6B8E23' }}>Africa Respiratory Care Association (ARCA)</h2>
                    <p className="mb-2"><strong>Last Updated/Effective Date:</strong> May 20, 2025</p>
                    <p className="mb-0">
                      <Mail size={16} className="me-2" style={{ color: '#6B8E23' }} />
                      <a href="mailto:info@africaresp.org" style={{ color: '#6B8E23', textDecoration: 'none' }}>
                        info@africaresp.org
                      </a>
                    </p>
                  </div>

                  {/* 1. Introduction */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>1. Introduction</h3>
                    <p className="text-muted">
                      The Africa Respiratory Care Association (ARCA) is committed to protecting the privacy and personal data of our members, partners, volunteers, website visitors, and all stakeholders. This Privacy Policy outlines how we collect, use, store, and safeguard your personal information in compliance with applicable data protection laws and best practices.
                    </p>
                  </div>

                  {/* 2. Who We Are */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>2. Who We Are</h3>
                    <p className="text-muted">
                      ARCA is a non-profit, volunteer-based organization dedicated to advancing respiratory care education, advocacy, and professional development across Africa. Our operations include managing member services, organizing events, promoting public awareness, and facilitating educational programs through digital and physical platforms.
                    </p>
                  </div>

                  {/* 3. Information We Collect */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>3. Information We Collect</h3>
                    <ul className="text-muted">
                      <li className="mb-2"><strong>Personal identification information:</strong> Full name, email address, phone number, mailing address, nationality, and professional title</li>
                      <li className="mb-2"><strong>Professional information:</strong> Qualifications, affiliations, and areas of expertise</li>
                      <li className="mb-2"><strong>Membership data:</strong> Membership category, payment status, event participation</li>
                      <li className="mb-2"><strong>Website usage data:</strong> IP addresses, browser type, referring/exit pages, and interaction data through cookies and analytics tools</li>
                      <li className="mb-2"><strong>Communication history:</strong> Inquiries, feedback, and email correspondence</li>
                    </ul>
                  </div>

                  {/* 4. How We Use Your Information */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>4. How We Use Your Information</h3>
                    <ul className="text-muted">
                      <li className="mb-2">Register and manage memberships</li>
                      <li className="mb-2">Send updates, newsletters, and event invitations</li>
                      <li className="mb-2">Organize webinars, conferences, and trainings</li>
                      <li className="mb-2">Maintain secure user accounts and access to member-only resources</li>
                      <li className="mb-2">Respond to inquiries and requests</li>
                      <li className="mb-2">Analyze website and program performance</li>
                      <li className="mb-2">Comply with legal and organizational obligations</li>
                    </ul>
                  </div>

                  {/* 5. Legal Basis for Processing */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>5. Legal Basis for Processing</h3>
                    <ul className="text-muted">
                      <li className="mb-2">Consent provided when individuals sign up for membership or communication</li>
                      <li className="mb-2">Contractual obligations related to event participation or service delivery</li>
                      <li className="mb-2">Legitimate interests in operating and improving ARCA's programs and outreach</li>
                      <li className="mb-2">Legal compliance with applicable nonprofit, financial, or data protection regulations</li>
                    </ul>
                  </div>

                  {/* 6. Data Sharing and Disclosure */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>6. Data Sharing and Disclosure</h3>
                    <p className="text-muted mb-2">
                      ARCA does not sell or share personal information with third parties for marketing purposes. We may share data with:
                    </p>
                    <ul className="text-muted">
                      <li className="mb-2">ARCA-approved partners or service providers who support our operations (e.g., payment processors, email platforms)</li>
                      <li className="mb-2">Legal authorities when required by law</li>
                      <li className="mb-2">Affiliated organizations or collaborators with consent</li>
                    </ul>
                  </div>

                  {/* 7. Data Security */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>7. Data Security</h3>
                    <p className="text-muted">
                      We implement appropriate physical, technical, and administrative safeguards to protect your personal information from unauthorized access, loss, misuse, or alteration. This includes secure hosting, encrypted communications, and restricted access to sensitive data.
                    </p>
                  </div>

                  {/* 8. Your Rights */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>8. Your Rights</h3>
                    <p className="text-muted mb-2">You have the right to:</p>
                    <ul className="text-muted">
                      <li className="mb-2">Access your personal data held by ARCA</li>
                      <li className="mb-2">Request correction or update of inaccurate information</li>
                      <li className="mb-2">Withdraw your consent at any time (where applicable)</li>
                      <li className="mb-2">Request the deletion of your data (subject to legal and contractual limitations)</li>
                      <li className="mb-2">Opt-out of receiving certain communications</li>
                    </ul>
                    <p className="text-muted">
                      To exercise these rights, please contact us at{' '}
                      <a href="mailto:info@africaresp.org" style={{ color: '#6B8E23', textDecoration: 'none' }}>
                        info@africaresp.org
                      </a>.
                    </p>
                  </div>

                  {/* 9. Cookies and Tracking */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>9. Cookies and Tracking</h3>
                    <p className="text-muted">
                      ARCA's website uses cookies and analytics tools to enhance user experience and monitor performance. Users may manage cookie preferences via their browser settings.
                    </p>
                  </div>

                  {/* 10. Data Retention */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>10. Data Retention</h3>
                    <p className="text-muted">
                      We retain personal information only as long as necessary to fulfill the purposes for which it was collected, or as required by law, after which it will be securely deleted or anonymized.
                    </p>
                  </div>

                  {/* 11. Third-Party Links */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>11. Third-Party Links</h3>
                    <p className="text-muted">
                      Our website and communications may contain links to external sites not operated by ARCA. We are not responsible for the privacy practices or content of those sites and encourage users to review their privacy policies.
                    </p>
                  </div>

                  {/* 12. Updates to This Policy */}
                  <div className="mb-4">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>12. Updates to This Policy</h3>
                    <p className="text-muted">
                      ARCA may update this Privacy Policy from time to time. Any changes will be posted on our website with a revised effective date. We encourage you to review this page periodically.
                    </p>
                  </div>

                  {/* 13. Contact Us */}
                  <div className="mb-0">
                    <h3 className="h5 fw-bold mb-3" style={{ color: '#2C5234' }}>13. Contact Us</h3>
                    <p className="text-muted mb-2">
                      If you have any questions or concerns regarding this Privacy Policy or the use of your personal data, please contact:
                    </p>
                    <div className="p-3 bg-light rounded">
                      <p className="mb-1 fw-bold" style={{ color: '#2C5234' }}>Africa Respiratory Care Association (ARCA)</p>
                      <p className="mb-0">
                        <Mail size={16} className="me-2" style={{ color: '#6B8E23' }} />
                        <a href="mailto:info@africaresp.org" style={{ color: '#6B8E23', textDecoration: 'none' }}>
                          info@africaresp.org
                        </a>
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default PrivacyPolicyPage;
